import React from 'react';
import { Menu, ChevronLeft } from '@design-systems/icons';
import cx from 'classnames';
import { ActionList, MenuItem } from '@mc/wink';
import { GLOBAL_NAV_LINKS } from '../../constants';
import { TranslateFocusedStatePageHeader } from '../../TranslateFocusedStatePageHeader';
import stylesheet from './HamburgerMenu.css';

interface Link {
  label: string;
  href: string;
  disabled?: boolean;
}

interface NavigationItem {
  label: string;
  uri: string;
}

interface ConfigLink {
  label: string;
  uri: string;
  children?: unknown[];
}

// Map navigation item names to their CSS class names
const getIconClassName = (label: string): string | null => {
  const labelLower = label.toLowerCase();

  // Direct mappings
  const iconMap: Record<string, string> = {
    home: 'home',
    campaigns: 'campaigns',
    automations: 'automations',
    sms: 'sms',
    forms: 'forms',
    audience: 'audience',
    analytics: 'analytics',
    'content studio': 'content-studio',
    content: 'content-studio', // Map "Content" to content-studio icon
    website: 'websites', // Map "Website" to websites icon
    websites: 'websites',
    integrations: 'integrations',
    projects: 'projects',
    'partner center': 'partner-center',
    help: 'help',
    search: 'search',
  };

  return iconMap[labelLower] || null;
};

interface HamburgerMenuProps {
  /** Array of navigation items to show at the top of the menu (max 2 items) */
  navigationItems?: NavigationItem[];
  /** Custom className for styling */
  className?: string;
}

const HamburgerMenu: React.FC<HamburgerMenuProps> = ({
  navigationItems = [],
  className,
}) => {
  // Get navigation items (max 2 items)
  const getNavigationItems = (): NavigationItem[] => {
    if (navigationItems?.length > 0) {
      // Ensure we never have more than 2 items
      return navigationItems.slice(0, 2);
    }
    return [];
  };

  // Function to safely get config
  const getConfig = () => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      return require('@mc/config').default;
    } catch (e) {
      // Config not available (e.g., in Storybook)
      return null;
    }
  };

  // Process global links from config with fallback to constants
  const getGlobalLinks = (): Link[] => {
    try {
      const config = getConfig();
      const modifiedGlobalLinks = [...config.globalLinks];
      // Remove the first item (create global link)
      modifiedGlobalLinks.shift();
      const globalLinks = modifiedGlobalLinks;

      // Check if config and globalLinks exist and have content
      if (
        globalLinks &&
        Array.isArray(config.globalLinks) &&
        config.globalLinks.length > 0
      ) {
        return globalLinks.map((link: ConfigLink) => ({
          label: link.label,
          href: link.uri,
          disabled: false,
        }));
      }
      // If config is empty or doesn't exist, fall through to constants
      throw new Error('Config globalLinks not available');
    } catch (e) {
      // Fallback to constants for Storybook and when config is not available
      return GLOBAL_NAV_LINKS.map((link: ConfigLink) => ({
        label: link.label,
        href: link.uri,
        disabled: false,
      }));
    }
  };

  const navigationItemsToShow = getNavigationItems();
  const globalLinks = getGlobalLinks();
  const translations = TranslateFocusedStatePageHeader();

  return (
    <div className={className}>
      <ActionList
        icon={<Menu size="small" />}
        size="medium"
        label="Page Header Menu"
        type="quaternary"
        tooltip={translations.menuTooltipLabel}
      >
        {/* Section 1: Navigation Items (max 2 items) */}
        {navigationItemsToShow.length > 0 &&
          navigationItemsToShow.map((item, index) => {
            return (
              <MenuItem
                key={`navigation-${index}`}
                label={item.label}
                href={item.uri}
                prefix={<ChevronLeft />}
              />
            );
          })}

        {/* Section 2: Global Navigation Links */}
        {globalLinks.map((link, index) => {
          const iconClass = getIconClassName(link.label);

          return (
            <MenuItem
              key={`global-${index}`}
              label={link.label}
              href={link.href}
              disabled={link.disabled}
              divider={index === 0}
              prefix={
                iconClass ? (
                  <span
                    className={cx(stylesheet.navIcon, stylesheet[iconClass])}
                  />
                ) : null
              }
            />
          );
        })}
      </ActionList>
    </div>
  );
};

export default HamburgerMenu;
